import { doc, runTransaction } from 'firebase/firestore'
import { firebaseAuth, firestoreDb } from './firebase'
import { logAuditEvent } from './auditLedgerService'
import type { Workspace } from '../context/WorkspaceContext'
import type { AnalysisCompareSide, AnalysisSnapshot, AnalysisSnapshotDoc, AnalysisViewMode } from '../types/analysis'
import type { ChartRecord } from '../components/Analysis/charts'

export const escapeCsvCell = (value: unknown) => {
  const text = value == null ? '' : String(value)
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

export const buildFilename = ({ prefix, workspace, suffix, extension, now = new Date() }: { prefix: string; workspace: Pick<Workspace, 'id' | 'slug'>; suffix: string; extension: string; now?: Date }) => {
  const stamp = now.toISOString().replace(/[-:]/g, '').slice(0, 13)
  return `${prefix}-${workspace.slug || workspace.id}-${suffix}-${stamp}.${extension}`
}

export const triggerBlobDownload = ({ blob, filename }: { blob: Blob; filename: string }) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.hidden = true
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  queueMicrotask(() => URL.revokeObjectURL(url))
}

export const writeExportAuditEvent = async ({ workspace, format, target, viewMode, moduleId, moduleTitle, snapshotId, channelId }: { workspace: Workspace; format: 'JSON' | 'CSV' | 'PNG' | 'PDF' | 'CHAT_CARD'; target: 'SNAPSHOT' | 'MODULE' | 'CURRENT_TAB'; viewMode: AnalysisViewMode; moduleId?: string; moduleTitle?: string; snapshotId?: string; channelId?: string }) => {
  const actorId = firebaseAuth.currentUser?.uid
  if (!actorId) throw new Error('A signed-in user is required to export analysis data.')
  await runTransaction(firestoreDb, async (transaction) => {
    const workspaceRef = doc(firestoreDb, 'workspaces', workspace.id)
    const workspaceSnapshot = await transaction.get(workspaceRef)
    logAuditEvent(firestoreDb, transaction, workspaceSnapshot.data(), {
      workspaceId: workspace.id, actorId, action: 'ANALYSIS_EXPORT', actionType: 'EXPORT', activityType: 'EXPORT', targetId: moduleId ?? snapshotId ?? workspace.id, nodeType: 'ANALYSIS_EXPORT', targetTitle: moduleTitle ?? 'Analysis snapshot', summary: `Exported ${target.toLowerCase()} as ${format}.`, payload: { format, target, moduleId, moduleTitle, snapshotId, channelId, viewMode },
    })
  })
}

export const exportSnapshotJson = async ({ workspace, snapshot, viewMode, snapshotId }: { workspace: Workspace; snapshot: AnalysisSnapshot | AnalysisSnapshotDoc; viewMode: AnalysisViewMode; snapshotId?: string }) => {
  const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' })
  triggerBlobDownload({ blob, filename: buildFilename({ prefix: 'analysis', workspace, suffix: 'snapshot', extension: 'json' }) })
  await writeExportAuditEvent({ workspace, format: 'JSON', target: 'SNAPSHOT', viewMode, snapshotId })
}

export const exportComparisonJson = async ({ workspace, sideA, sideB }: { workspace: Workspace; sideA: AnalysisCompareSide; sideB: AnalysisCompareSide }) => {
  const blob = new Blob([JSON.stringify({ generatedAt: new Date().toISOString(), sideA, sideB }, null, 2)], { type: 'application/json' })
  triggerBlobDownload({ blob, filename: buildFilename({ prefix: 'analysis', workspace, suffix: 'comparison', extension: 'json' }) })
  await writeExportAuditEvent({ workspace, format: 'JSON', target: 'SNAPSHOT', viewMode: 'COMPARE' })
}

export const exportModuleCsv = async ({ workspace, moduleId, moduleTitle, records, snapshotId, viewMode = 'LIVE' }: { workspace: Workspace; moduleId: string; moduleTitle: string; records: ChartRecord[]; snapshotId?: string; viewMode?: AnalysisViewMode }) => {
  const content = ['id,primary,secondary,numeric,drillTarget', ...records.map((record) => [record.id, record.primary, record.secondary, record.numeric, record.drillTarget].map(escapeCsvCell).join(','))].join('\r\n')
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  triggerBlobDownload({ blob, filename: buildFilename({ prefix: 'analysis', workspace, suffix: moduleId, extension: 'csv' }) })
  await writeExportAuditEvent({ workspace, format: 'CSV', target: 'MODULE', viewMode, moduleId, moduleTitle, snapshotId })
}

// These mirror the stable light/dark surface colors used by the app shell.
export const resolveExportBackground = () => {
  const declared = document.documentElement.getAttribute('data-theme')
  const dark = declared === 'dark' || (!declared && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  return dark ? { hex: '#161b22', theme: 'dark' as const } : { hex: '#ffffff', theme: 'light' as const } // mosaic-keep: data (export png background hex)
}

export const svgToPngBlob = (svg: SVGElement, scale: number, background: string): Promise<Blob> => new Promise((resolve, reject) => {
  const box = svg.getBoundingClientRect()
  if (!box.width || !box.height) { reject(new Error('Chart SVG has no rendered dimensions.')); return }
  const clone = svg.cloneNode(true) as SVGElement
  clone.setAttribute('width', String(box.width))
  clone.setAttribute('height', String(box.height))
  const sourceNodes = [svg, ...Array.from(svg.querySelectorAll('*'))]
  const cloneNodes = [clone, ...Array.from(clone.querySelectorAll('*'))]
  sourceNodes.forEach((node, index) => { const target = cloneNodes[index]; if (target) target.setAttribute('style', getComputedStyle(node).cssText) })
  const source = `<?xml version="1.0" encoding="UTF-8"?>${new XMLSerializer().serializeToString(clone)}`
  const image = new Image()
  image.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(box.width * scale); canvas.height = Math.round(box.height * scale)
    const context = canvas.getContext('2d')
    if (!context) { reject(new Error('Canvas is unavailable.')); return }
    context.fillStyle = background; context.fillRect(0, 0, canvas.width, canvas.height); context.setTransform(scale, 0, 0, scale, 0, 0); context.drawImage(image, 0, 0, box.width, box.height)
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('PNG capture failed.')), 'image/png', 1)
  }
  image.onerror = () => reject(new Error('Chart SVG could not be rendered.'))
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`
})

export const exportModuleChartPng = async ({ workspace, moduleId, moduleTitle, svgElement, viewMode, snapshotId }: { workspace: Workspace; moduleId: string; moduleTitle: string; svgElement: SVGElement | null; viewMode: AnalysisViewMode; snapshotId?: string }) => {
  if (!svgElement) throw new Error('Chart SVG is not ready.')
  const background = resolveExportBackground()
  const blob = await svgToPngBlob(svgElement, 2, background.hex)
  triggerBlobDownload({ blob, filename: buildFilename({ prefix: 'analysis', workspace, suffix: moduleId, extension: 'png' }) })
  await writeExportAuditEvent({ workspace, format: 'PNG', target: 'MODULE', viewMode, moduleId, moduleTitle, snapshotId })
}
